import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";

export default function Footer() {
  const { t, dir } = useLang();
  const flip = dir === "rtl" ? "scaleX(-1)" : "none";

  return (
    <footer className="rq-dark relative mt-0 overflow-hidden border-t border-white/10" style={{ background: "linear-gradient(160deg,#0a1f3c 0%,#071528 100%)" }}>
      <div className="rq-grid-bg absolute inset-0 opacity-[0.12]" />
      <div
        className="absolute -top-24 ltr:right-0 rtl:left-0 h-80 w-80 rounded-full blur-[120px] opacity-30"
        style={{ background: "radial-gradient(circle,#14a24b,transparent 70%)" }}
      />

      {/* main footer */}
      <div className="relative rq-container" style={{ paddingTop: "44px", paddingBottom: "52px" }}>
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <img src="/roboquess-logo-new.webp" alt="RoboQuess" className="h-11 object-contain" style={{ filter: "brightness(0) invert(1)" }} />
            <p className="mt-5 text-sm leading-relaxed text-[#9fb1c6] max-w-xs">{t.footer.desc}</p>
            <div className="mt-6 flex gap-3">
              {[Linkedin, Twitter, Facebook].map((Icon, i) => (
                <a key={i} href="#" className="grid place-items-center h-10 w-10 rounded-lg border border-white/15 text-[#7be0a8] transition-all duration-300 hover:bg-white/10 hover:-translate-y-1">
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-5">{t.footer.quickLinks}</h4>
            <ul className="space-y-3 text-sm text-[#9fb1c6]">
              {[
                { to: "/about", label: t.nav.about },
                { to: "/why-us", label: t.nav.why },
                { to: "/partners", label: t.nav.partners },
                { to: "/careers", label: t.nav.careers },
                { to: "/blog", label: t.nav.resources },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="inline-flex items-center gap-1.5 transition-colors duration-300 hover:text-[#7be0a8]">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-5">{t.footer.servicesTitle}</h4>
            <ul className="space-y-3 text-sm text-[#9fb1c6]">
              {t.servicesList.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="transition-colors duration-300 hover:text-[#7be0a8]">{s.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-5">{t.footer.contactTitle}</h4>
            <ul className="space-y-4 text-sm text-[#9fb1c6]">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#7be0a8] mt-0.5 shrink-0" />
                <span className="whitespace-pre-line leading-relaxed">{t.contactPage.addressValue}</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#7be0a8] shrink-0" />
                <a href="mailto:contactus@roboquess.ae" className="hover:text-[#7be0a8]">contactus@roboquess.ae</a>
              </li>
              <li className="flex items-start gap-3" dir="ltr">
                <Phone size={18} className="text-[#7be0a8] shrink-0 mt-0.5" />
                <span className="flex flex-col">
                  <a href="tel:+971569818938" className="hover:text-[#7be0a8]">+971 56 981 8938</a>
                  <a href="tel:+971507512496" className="hover:text-[#7be0a8]">+971 50 751 2496</a>
                </span>
              </li>
            </ul>
            <div className="mt-6">
              <p className="text-xs mb-2 text-[#9fb1c6]">{t.footer.newsletter}</p>
              <div className="flex gap-2">
                <input type="email" placeholder={t.footer.emailPlaceholder} className="flex-1 rounded-lg bg-white/5 border border-white/15 px-3 py-2 text-sm text-white placeholder:text-[#7085a0] outline-none focus:border-[#7be0a8]" />
                <button className="rq-glow-btn px-3 grid place-items-center">
                  <ArrowRight size={18} style={{ transform: flip }} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* oversized brand wordmark (Robiquity-style) */}
        <div className="mt-16 mb-2 select-none pointer-events-none overflow-hidden text-center">
          <div
            className="font-display font-bold leading-[0.8] tracking-tighter whitespace-nowrap"
            style={{ fontSize: "clamp(56px, 14vw, 210px)", color: "rgba(123,224,168,0.22)" }}
          >
            RoboQuess
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#8496ad]">
          <span>{t.footer.rights}</span>
          <a href="https://techbook.co.in/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 transition-all duration-300 hover:border-[#7be0a8] hover:bg-white/10">
            <span className="h-2 w-2 rounded-full bg-[#7be0a8] animate-pulse" />
            {t.footer.poweredBy}{" "}
            <span className="font-semibold text-[#7be0a8]">Techbook Technologies</span>
          </a>
        </div>
      </div>
    </footer>
  );
}
